import { useMemo } from 'react';
import { AvailabilityGroup } from '@weave/schema-gen-ts/dist/schemas/ai-settings/v1/ai_settings.pb';
import dayjs from 'dayjs';
import { V1 } from '@frontend/api-ai-settings';
import { CalendarEvent } from '@frontend/calendar-grid';
import { DATETIME_FORMATS } from '../../constants';
import { AvailabilityFilters, TimeRange } from './week-view-availability-header/availability-header.types';

const getTimeRangeForSlot = (slotHour: number, timeRanges: TimeRange[]) =>
  timeRanges.find(({ startHour, endHour }) => slotHour >= startHour && slotHour < endHour);

interface UseGetWeeklyAvailabilitySlotsProps {
  availabilityFilters: AvailabilityFilters;
  orgId: string;
  locationId: string;
}

const convertApiSlotTime = (time: string, format: string = DATETIME_FORMATS.US_TIME) =>
  dayjs(time, DATETIME_FORMATS.ISO_TIME).format(format);

const convertApiSlotsIntoDateWiseEvents = (
  availabilityGroups?: AvailabilityGroup[],
  selectedTimeRanges?: TimeRange[]
): Record<string, { id: string; start: string; label: string }[]> => {
  if (!availabilityGroups) return {};

  return availabilityGroups.reduce<Record<string, { id: string; start: string; label: string }[]>>(
    (acc, availabilityGroup) => {
      availabilityGroup.slots?.forEach((availabilitySlot, slotIndex) => {
        const date = availabilitySlot.slot?.date;
        const startTime = availabilitySlot.slot?.slot?.start;

        if (!date || !startTime) {
          return;
        }

        const matchedTimeRange = getTimeRangeForSlot(
          dayjs(startTime, DATETIME_FORMATS.ISO_TIME).hour(),
          selectedTimeRanges ?? []
        );

        if (!matchedTimeRange) {
          return;
        }

        acc[date] ??= [];
        acc[date].push({
          id: availabilitySlot.id || `${availabilityGroup.id}-${date}-${startTime}-${slotIndex}`,
          start: startTime,
          label: matchedTimeRange.label,
        });
      });

      return acc;
    },
    {}
  );
};

export const useGetWeeklyAvailabilitySlots = ({
  availabilityFilters,
  orgId,
  locationId,
}: UseGetWeeklyAvailabilitySlotsProps) => {
  const availabilityGroups = V1.Queries.useListAvailabilityGroupsQuery({
    request: {
      orgId,
      locationId,
      serviceId: availabilityFilters.serviceId,
      staffIds: availabilityFilters.providerIds,
      resourceIds: availabilityFilters.operatoryIds,
      startDate: availabilityFilters.weekStartDate,
      endDate: availabilityFilters.weekEndDate,
    },
    options: { enabled: !!availabilityFilters.serviceId && !!orgId && !!locationId },
  });

  const { columns, events } = useMemo(() => {
    const filteredSlotsByDate = convertApiSlotsIntoDateWiseEvents(
      availabilityGroups.data?.availabilityGroups,
      availabilityFilters.timeRanges
    );

    const calendarEvents: CalendarEvent[] = [];
    const eventIdsByDate: Record<string, string[]> = {};

    Object.entries(filteredSlotsByDate).forEach(([date, slots]) => {
      const slotsByTimeRange = slots.reduce<Record<string, typeof slots>>((acc, slot) => {
        acc[slot.label] ??= [];
        acc[slot.label].push(slot);
        return acc;
      }, {});

      availabilityFilters.timeRanges.forEach(({ label, startHour: timeRangeStartHour }) => {
        const slotsInRange = (slotsByTimeRange[label] ?? []).sort((left, right) =>
          left.start.localeCompare(right.start)
        );

        if (slotsInRange.length === 0) {
          return;
        }

        const labelEventId = `${date}-${label.replace(/\s+/g, '-')}-label`;

        eventIdsByDate[date] ??= [];
        eventIdsByDate[date].push(labelEventId);

        calendarEvents.push({
          id: labelEventId,
          eventType: 'slotsStartLabel',
          startTime: dayjs().hour(timeRangeStartHour).minute(0).format(DATETIME_FORMATS.US_TIME),
          duration: 0, // Duration is not needed for label events as they will be displayed as fixed-size indicators
          label,
        });

        slotsInRange.forEach((slot) => {
          const eventId = `${date}-${slot.id}`;
          eventIdsByDate[date].push(eventId);
          calendarEvents.push({
            id: eventId,
            eventType: 'nonInteractiveSlot',
            startTime: convertApiSlotTime(slot.start),
            duration: 0, // Duration is not needed for non-interactive slots as they will be displayed as fixed-size indicators
          });
        });
      });
    });

    return {
      columns: Array.from({ length: 7 }, (_, index) => {
        const date = dayjs(availabilityFilters.weekStartDate, DATETIME_FORMATS.ISO_DATE).add(index, 'day');
        const dateKey = date.format(DATETIME_FORMATS.ISO_DATE);

        return {
          id: `availability-column-${dateKey}`,
          name: date.format('dddd'),
          dayOfMonth: date.date(),
          slotsCount: filteredSlotsByDate[dateKey]?.length ?? 0,
          eventIds: eventIdsByDate[dateKey] ?? [],
          groupId: locationId,
        };
      }),
      events: calendarEvents,
    };
  }, [
    availabilityGroups.data?.availabilityGroups,
    availabilityFilters.timeRanges,
    availabilityFilters.weekStartDate,
    locationId,
  ]);

  return { columns, events, isLoading: availabilityGroups.isFetching };
};
